import type { ReactNode } from 'react';
import { scanProblemFor } from '../renderer/LiveScanFeed';
import type { ScanAgentDevice, ScanOutcomeCode } from './scan-agent';
import { wordsForOutcome } from './scan-outcomes';

export interface ScanProblemNoteProps {
  problem: string;
  devices: readonly ScanAgentDevice[];
  device: string;
  /** The agent's outcome code (Paige Scan 1.5.2 and later); its words win when this page knows it. */
  outcome?: ScanOutcomeCode | null;
  /** Pages the scan handed over before it ended: the words say they are kept. */
  pages?: number;
}

/**
 * A scan that ended badly, said the way a person can act on it. With Paige Scan's outcome code (a jam,
 * paper out, the scanner gone) the note says what happened, that the pages so far are kept, and what to
 * do in order (scan-outcomes). Without one (an older Paige Scan, a scanner that will not start) it is
 * CapturePoint's own wording for a scanner that cannot start (0c71e0d0, `scanProblemFor`), fed with
 * Paige Scan's refusal, with the scanner's own message below when it adds anything.
 */
export function ScanProblemNote({
  problem,
  devices,
  device,
  outcome,
  pages = 0,
}: ScanProblemNoteProps): ReactNode {
  const name = devices.find((item) => item.id === device)?.name ?? 'The scanner';
  const words = wordsForOutcome(outcome ?? undefined, pages);
  const said = words ? { ...words, detail: null } : scanProblemFor(name, problem);
  return (
    <div className="paige-scan-problem" role="alert">
      <strong>{said.title}</strong>
      <ol>
        {said.steps.map((step) => (
          <li key={step}>{step}</li>
        ))}
      </ol>
      {said.detail && said.detail !== said.title && <small>{said.detail}</small>}
    </div>
  );
}
